/**
 * Global authentication context — Cognito-backed.
 *
 * On mount, attempts to restore a Cognito session from local storage.
 * Exposes login / register / logout plus a `needsConfirmation` flag
 * so pages can show "pending approval" UI.
 *
 * Listens for the custom `auth:expired` event from api.client (401)
 * and auto-logs the user out when the token can't be refreshed.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { User, LoginCredentials, RegisterData } from '../types';
import * as authService from '../services/auth.service';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  needsConfirmation: boolean;
  needsNewPassword: boolean;
  registrationSuccess: boolean;
  login: (credentials: LoginCredentials) => Promise<boolean>;
  register: (data: RegisterData) => Promise<boolean>;
  completeNewPassword: (newPassword: string) => Promise<boolean>;
  logout: () => Promise<void>;
  error: string | null;
  clearError: () => void;
  clearRegistrationSuccess: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // Bootstrap: restore existing Cognito session
  useEffect(() => {
    authService
      .restoreSession()
      .then((res) => {
        if (res.success && res.data) {
          setUser(res.data);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  // Listen for 401 (auth:expired) events from api.client
  useEffect(() => {
    const handleExpired = () => {
      authService.logout();
      setUser(null);
      setError('Your session has expired. Please sign in again.');
    };
    window.addEventListener('auth:expired', handleExpired);
    return () => window.removeEventListener('auth:expired', handleExpired);
  }, []);

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<boolean> => {
      setIsLoading(true);
      setError(null);
      setNeedsConfirmation(false);
      setNeedsNewPassword(false);

      try {
        const response = await authService.login(credentials);

        if (response.success && response.data) {
          setUser(response.data);
          return true;
        }

        // Admin-created account requires a new password
        if (response.success && response.needsNewPassword) {
          setNeedsNewPassword(true);
          return false;
        }

        // Account exists but not yet confirmed by admin
        if (response.needsConfirmation) {
          setNeedsConfirmation(true);
        }

        setError(response.error ?? 'Sign-in failed');
        return false;
      } catch {
        setError('An unexpected error occurred');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const register = useCallback(
    async (data: RegisterData): Promise<boolean> => {
      setIsLoading(true);
      setError(null);
      setRegistrationSuccess(false);

      try {
        const response = await authService.register(data);

        if (response.success) {
          // Sign-up succeeded — user is UNCONFIRMED
          // Do NOT auto-login. Show pending approval message.
          setRegistrationSuccess(true);
          return true;
        }

        setError(response.error ?? 'Registration failed');
        return false;
      } catch {
        setError('An unexpected error occurred');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const completeNewPassword = useCallback(
    async (newPassword: string): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await authService.completeNewPassword(newPassword);

        if (response.success && response.data) {
          setNeedsNewPassword(false);
          setUser(response.data);
          return true;
        }

        setError(response.error ?? 'Failed to set new password');
        return false;
      } catch {
        setError('An unexpected error occurred');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
    setNeedsConfirmation(false);
    setNeedsNewPassword(false);
    setRegistrationSuccess(false);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
    setNeedsConfirmation(false);
    setNeedsNewPassword(false);
  }, []);

  const clearRegistrationSuccess = useCallback(() => {
    setRegistrationSuccess(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        needsConfirmation,
        needsNewPassword,
        registrationSuccess,
        login,
        register,
        completeNewPassword,
        logout,
        error,
        clearError,
        clearRegistrationSuccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
